import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppEquipmentTypeSelectField, AppInputField, AppSelectField } from '@/form/FormUI';
import type { EquipmentListNodeType, NodeFormProps } from '@/nodes';
import { equipmentTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { EquipmentListDataSchema, type EquipmentListDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof EquipmentListDataSchema>;

export const EquipmentListForm: FC<NodeFormProps<EquipmentListNodeType>> = ({
    spaceId,
    flowId,
    node,
    updateNode,
    cancelUpdate,
}) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, EquipmentListDataType>({
        resolver: zodResolver(EquipmentListDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: EquipmentListDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppEquipmentTypeSelectField
                        label="Show Only"
                        control={form.control}
                        name="onlyEquipmentTypeId"
                        description="Only show equipment of this type"
                        equipmentTypes={equipmentTypes}
                        placeholder="Select Equipment Type (Optional)"
                        allowContextSelection
                    />

                    <AppSelectField
                        label="Grouping"
                        control={form.control}
                        name="grouping"
                        options={['none', 'by-room']}
                        description="How should the equipment be grouped?"
                    />

                    <AppInputField
                        label="Add Initial Item Text"
                        placeholder="Go to the first item in your home, then click the button below."
                        control={form.control}
                        name="emptyText"
                        description="The text to show initially when there are no items added yet."
                    />

                    <AppInputField
                        label="Add Initial Item Button Text"
                        placeholder="Add First Item"
                        control={form.control}
                        name="addFirstLabel"
                        description="The label on the button to add the first item."
                    />

                    <AppInputField
                        label="Add Another Label"
                        placeholder="Add Another Item"
                        control={form.control}
                        name="addLabel"
                        description="The label on the button to add more items."
                    />

                    <AppInputField
                        label="Done Label"
                        placeholder="Done Adding Equipment"
                        control={form.control}
                        name="doneLabel"
                        description="The label on the link to be done adding equipment."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
